import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import Layout from "../components/layout/CompliceLayout";
import {
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

// Sample Audit Log Data
const initialAuditLogs = [
  {
    id: 1,
    timestamp: "2024-06-15 10:30:45",
    user: "John Doe",
    action: "Created Financial Report",
    module: "Financial Management",
    status: "Success",
    ipAddress: "192.168.1.100",
    details: "Quarterly financial report generated for Q2 2024",
  },
  {
    id: 2,
    timestamp: "2024-06-14 15:22:11",
    user: "Jane Smith",
    action: "Updated User Permissions",
    module: "User Management",
    status: "Warning",
    ipAddress: "10.0.0.55",
    details: "Modified access rights for marketing team",
  },
  {
    id: 3,
    timestamp: "2024-06-13 09:45:30",
    user: "Mike Johnson",
    action: "Deleted Temporary Data",
    module: "System Maintenance",
    status: "Error",
    ipAddress: "172.16.0.75",
    details: "Encountered issue while clearing temporary cache",
  },
];

// Status Chip Component
const AuditStatusChip = ({ status }) => {
  const getChipProps = () => {
    switch (status) {
      case "Success":
        return { color: "success", icon: <SuccessIcon /> };
      case "Warning":
        return { color: "warning", icon: <WarningIcon /> };
      case "Error":
        return { color: "error", icon: <ErrorIcon /> };
      default:
        return { color: "default" };
    }
  };

  const chipProps = getChipProps();

  return <Chip label={status} {...chipProps} variant="outlined" size="small" />;
};

const CompliceAudit = () => {
  const [auditLogs, setAuditLogs] = useState(initialAuditLogs);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [filters, setFilters] = useState({
    user: "",
    module: "",
    status: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Audit Details Modal
  const handleOpenDetails = (audit) => {
    setSelectedAudit(audit);
  };

  const handleCloseDetails = () => {
    setSelectedAudit(null);
  };

  // Filtering and Searching
  const filteredAudits = auditLogs.filter((log) => {
    const matchesUser = filters.user
      ? log.user.toLowerCase().includes(filters.user.toLowerCase())
      : true;
    const matchesModule = filters.module ? log.module === filters.module : true;
    const matchesStatus = filters.status ? log.status === filters.status : true;
    const matchesSearch = searchTerm
      ? Object.values(log).some((value) =>
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      : true;

    return matchesUser && matchesModule && matchesStatus && matchesSearch;
  });
  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Audit Trail Management
        </Typography>

        {/* Filtering and Search Section */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Search Logs"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Filter by User"
              value={filters.user}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, user: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Module</InputLabel>
              <Select
                value={filters.module}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, module: e.target.value }))
                }
                label="Module"
              >
                <MenuItem value="">All Modules</MenuItem>
                <MenuItem value="Financial Management">
                  Financial Management
                </MenuItem>
                <MenuItem value="User Management">User Management</MenuItem>
                <MenuItem value="System Maintenance">
                  System Maintenance
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Success">Success</MenuItem>
                <MenuItem value="Warning">Warning</MenuItem>
                <MenuItem value="Error">Error</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Audit Logs Table */}
        <Paper elevation={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAudits.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{log.timestamp}</TableCell>
                    <TableCell>{log.user}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>{log.module}</TableCell>
                    <TableCell>
                      <AuditStatusChip status={log.status} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDetails(log)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Audit Details Dialog */}
        <Dialog
          open={!!selectedAudit}
          onClose={handleCloseDetails}
          maxWidth="md"
          fullWidth
        >
          {selectedAudit && (
            <>
              <DialogTitle>Audit Log Details</DialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1">
                      <strong>Timestamp:</strong> {selectedAudit.timestamp}
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>User:</strong> {selectedAudit.user}
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>IP Address:</strong> {selectedAudit.ipAddress}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1">
                      <strong>Module:</strong> {selectedAudit.module}
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Action:</strong> {selectedAudit.action}
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Status:</strong>{" "}
                      <AuditStatusChip status={selectedAudit.status} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      <strong>Details:</strong> {selectedAudit.details}
                    </Typography>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDetails} color="primary">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </Layout>
  );
};

export default CompliceAudit;
